import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Space, Typography, message, Tag, Button, Empty, Statistic, Row, Col, Drawer, Timeline, Collapse, Tabs, Popconfirm } from 'antd';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  History,
  RefreshCw,
  Rocket,
  Loader2,
  Bot,
  Wrench,
  Plug,
  BarChart3,
  LineChart,
  LayoutDashboard,
} from 'lucide-react';
import { useCrewStore } from '../stores/useCrewStore';
import { apiService } from '../services/api';
import { CrewRun, RunStep } from '../types';
import type { ColumnsType } from 'antd/es/table';
import { getImageModelById } from '../constants/imageModels';
import { getModelById } from '../constants/models';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const ResultsPage: React.FC = () => {
  const { crews } = useCrewStore();
  const [runs, setRuns] = useState<CrewRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState<CrewRun | null>(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [deletingWordpressRunId, setDeletingWordpressRunId] = useState<string | null>(null);

  const getImageModelMeta = (imageModelId?: string) => {
    if (!imageModelId) return null;
    const model = getImageModelById(imageModelId);
    if (!model) {
      return { name: imageModelId, subline: undefined };
    }

    const details = [model.endpoint, model.speedCategory, model.pricing.startingAt]
      .filter(Boolean)
      .join(' • ');

    return {
      name: model.name,
      subline: details,
    };
  };

  const getLanguageModelMeta = (modelId?: string) => {
    if (!modelId) return null;
    const model = getModelById(modelId);
    if (!model) {
      return { name: modelId, provider: undefined, description: undefined };
    }

    const details = [model.description, model.contextWindow, model.performance]
      .filter(Boolean)
      .join(' • ');

    return {
      name: model.name,
      provider: model.provider,
      description: details,
    };
  };

  useEffect(() => {
    loadRuns();
  }, []);

  // Auto-refresh every 10 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadRuns();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadRuns = async () => {
    setLoading(true);
    try {
      const data = await apiService.getCrewRuns();
      setRuns(data);
    } catch (error) {
      message.error('Failed to load crew runs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRun = (run: CrewRun) => {
    setSelectedRun(run);
    setIsDrawerVisible(true);
  };

  const handleDeleteWordpressPost = async (run: CrewRun) => {
    if (!run) return;
    setDeletingWordpressRunId(run.id);
    try {
      await apiService.deleteWordpressPost(run.id, run.wordpress_post?.id);
      message.success('WordPress post deleted');
      setRuns((prevRuns) =>
        prevRuns.map((item) =>
          item.id === run.id ? { ...item, wordpress_post: undefined } : item
        )
      );
      setSelectedRun((prev) =>
        prev && prev.id === run.id ? { ...prev, wordpress_post: undefined } : prev
      );
    } catch (error) {
      message.error('Failed to delete WordPress post');
      console.error(error);
    } finally {
      setDeletingWordpressRunId(null);
    }
  };

  const getStatusTag = (status: CrewRun['status']) => {
    const statusConfig = {
      completed: { color: 'success', icon: <CheckCircleOutlined /> },
      running: { color: 'processing', icon: <ClockCircleOutlined /> },
      failed: { color: 'error', icon: <ExclamationCircleOutlined /> },
      pending: { color: 'default', icon: <ClockCircleOutlined /> },
    };

    const config = statusConfig[status];
    return (
      <Tag icon={config.icon} color={config.color}>
        {status.toUpperCase()}
      </Tag>
    );
  };

  const formatDuration = (startTime?: string, endTime?: string) => {
    if (!startTime) return 'N/A';

    const start = new Date(startTime).getTime();
    const end = endTime ? new Date(endTime).getTime() : Date.now();
    const duration = (end - start) / 1000; // in seconds

    if (duration < 60) return `${duration.toFixed(0)}s`;
    if (duration < 3600) return `${Math.floor(duration / 60)}m ${Math.floor(duration % 60)}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  const getCrewName = (crewId: string) => {
    const crew = crews.find(c => c.id === crewId);
    return crew?.name || 'Unknown Crew';
  };

  const getInputLabel = (inputs?: Record<string, any>) => {
    if (!inputs) return 'No inputs';

    // Try to extract meaningful label from inputs
    if (inputs.keyword) return inputs.keyword;
    if (inputs.city) return inputs.city;
    if (inputs.topic) return inputs.topic;
    if (inputs.url) return inputs.url;

    return Object.keys(inputs).length > 0 ? `${Object.keys(inputs).length} params` : 'No inputs';
  };

  const getStepIcon = (step: RunStep) => {
    if (step.status === 'running') return <LoadingOutlined />;
    if (step.status === 'failed') return <ExclamationCircleOutlined />;
    if (step.status === 'completed') return <CheckCircleOutlined />;

    switch (step.step_type) {
      case 'agent': return <RobotOutlined />;
      case 'tool': return <ToolOutlined />;
      case 'llm-call': return <ApiOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getStepStatusColor = (status: RunStep['status']) => {
    switch (status) {
      case 'completed': return 'green';
      case 'running': return 'blue';
      case 'failed': return 'red';
      case 'pending': return 'gray';
      default: return 'gray';
    }
  };

  const formatStepDuration = (durationMs?: number) => {
    if (!durationMs) return 'N/A';
    const seconds = durationMs / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`;
  };

  // Mock steps for demonstration when backend doesn't provide them yet
  const getMockSteps = (run: CrewRun): RunStep[] => {
    if (run.steps && run.steps.length > 0) return run.steps;

    // Generate mock steps based on typical location page creation workflow
    const baseSteps: Omit<RunStep, 'id'>[] = [
      {
        step_type: 'task',
        name: 'Generate Image Prompts',
        agent_name: 'Image Prompt Agent',
        status: run.status === 'pending' ? 'pending' : 'completed',
        started_at: run.started_at,
        summary: 'Generated AI image prompts for the location page',
      },
      {
        step_type: 'task',
        name: 'Generate Page Headings',
        agent_name: 'Headings Agent',
        status: run.status === 'pending' ? 'pending' : run.status === 'running' ? 'running' : 'completed',
        summary: 'Created SEO-optimized headings structure',
      },
      {
        step_type: 'task',
        name: 'Generate Meta Description',
        agent_name: 'Meta Description Agent',
        status: run.status === 'completed' ? 'completed' : 'pending',
        summary: 'Crafted meta description for search engines',
      },
      {
        step_type: 'task',
        name: 'Generate Content Sections',
        agent_name: 'Content Sections Agent',
        status: run.status === 'completed' ? 'completed' : 'pending',
        summary: 'Generated detailed content for each section',
      },
      {
        step_type: 'task',
        name: 'Assemble Final Page Data',
        agent_name: 'Page Assembler',
        status: run.status === 'completed' ? 'completed' : 'pending',
        summary: 'Combined all components into final page structure',
      },
    ];

    return baseSteps.map((step, idx) => ({
      ...step,
      id: `mock-step-${idx}`,
    }));
  };

  const columns: ColumnsType<CrewRun> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: CrewRun['status']) => getStatusTag(status),
      filters: [
        { text: 'Completed', value: 'completed' },
        { text: 'Running', value: 'running' },
        { text: 'Failed', value: 'failed' },
        { text: 'Pending', value: 'pending' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Language Model',
      dataIndex: 'model_id',
      key: 'model_id',
      width: 240,
      render: (_: unknown, record: CrewRun) => {
        const meta = getLanguageModelMeta(record.model_id);
        if (!meta) {
          return <Text type="secondary">Default</Text>;
        }

        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              {meta.provider && (
                <Tag color="blue" style={{ fontSize: 10, padding: '0 4px' }}>
                  {meta.provider.toUpperCase()}
                </Tag>
              )}
              <Text strong>{meta.name}</Text>
            </Space>
            {meta.description && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {meta.description.length > 60 ? `${meta.description.substring(0, 60)}...` : meta.description}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Image Model',
      dataIndex: 'image_model_id',
      key: 'image_model_id',
      width: 240,
      render: (_: unknown, record: CrewRun) => {
        const meta = getImageModelMeta(record.image_model_id);
        if (!meta) {
          return <Text type="secondary">Not used</Text>;
        }

        return (
          <Space direction="vertical" size={0}>
            <Text strong>{meta.name}</Text>
            {meta.subline && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {meta.subline.length > 60 ? `${meta.subline.substring(0, 60)}...` : meta.subline}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Crew',
      dataIndex: 'crew_id',
      key: 'crew',
      render: (crewId: string) => <Text strong>{getCrewName(crewId)}</Text>,
    },
    {
      title: 'Input',
      dataIndex: 'inputs',
      key: 'inputs',
      render: (inputs: Record<string, any>) => (
        <Text type="secondary">{getInputLabel(inputs)}</Text>
      ),
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 180,
      render: (time?: string) => time ? new Date(time).toLocaleString() : 'N/A',
      sorter: (a, b) => {
        if (!a.started_at || !b.started_at) return 0;
        return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
      },
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 120,
      render: (_, record) => (
        <Space>
          <ClockCircleOutlined />
          <Text>{formatDuration(record.started_at, record.completed_at)}</Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewRun(record)}
        >
          View
        </Button>
      ),
    },
  ];

  const runningCount = runs.filter(r => r.status === 'running').length;
  const completedCount = runs.filter(r => r.status === 'completed').length;
  const failedCount = runs.filter(r => r.status === 'failed').length;

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalRuns = runs.length;
    const successRate = totalRuns > 0 ? ((completedCount / totalRuns) * 100).toFixed(1) : '0';
    const failureRate = totalRuns > 0 ? ((failedCount / totalRuns) * 100).toFixed(1) : '0';

    // Calculate average duration for completed runs
    const completedRuns = runs.filter(r => r.status === 'completed' && r.started_at && r.completed_at);
    const avgDuration = completedRuns.length > 0
      ? completedRuns.reduce((sum, run) => {
          const start = new Date(run.started_at!).getTime();
          const end = new Date(run.completed_at!).getTime();
          return sum + ((end - start) / 1000);
        }, 0) / completedRuns.length
      : 0;

    // Calculate P90 duration
    const sortedDurations = completedRuns
      .map(run => {
        const start = new Date(run.started_at!).getTime();
        const end = new Date(run.completed_at!).getTime();
        return (end - start) / 1000;
      })
      .sort((a, b) => a - b);
    const p90Duration = sortedDurations.length > 0
      ? sortedDurations[Math.floor(sortedDurations.length * 0.9)]
      : 0;

    // Group runs by date for trend analysis
    const runsByDate = runs.reduce((acc, run) => {
      if (!run.started_at) return acc;
      const date = new Date(run.started_at).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { total: 0, completed: 0, failed: 0, running: 0, pending: 0 };
      }
      acc[date].total++;
      acc[date][run.status]++;
      return acc;
    }, {} as Record<string, { total: number; completed: number; failed: number; running: number; pending: number }>);

    // Group runs by crew
    const runsByCrew = runs.reduce((acc, run) => {
      const crewName = getCrewName(run.crew_id);
      if (!acc[crewName]) {
        acc[crewName] = { total: 0, completed: 0, failed: 0 };
      }
      acc[crewName].total++;
      if (run.status === 'completed') acc[crewName].completed++;
      if (run.status === 'failed') acc[crewName].failed++;
      return acc;
    }, {} as Record<string, { total: number; completed: number; failed: number }>);

    return {
      totalRuns,
      successRate,
      failureRate,
      avgDuration,
      p90Duration,
      runsByDate,
      runsByCrew,
    };
  }, [runs, completedCount, failedCount]);

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>
            <HistoryOutlined /> Agent Ops Monitoring
          </Title>
          <Paragraph type="secondary">
            Track and monitor crew execution runs in real-time
          </Paragraph>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadRuns}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type={autoRefresh ? 'primary' : 'default'}
            icon={<ClockCircleOutlined />}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Runs"
              value={runs.length}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Running"
              value={runningCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Completed"
              value={completedCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Failed"
              value={failedCount}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="runs"
        items={[
          {
            key: 'runs',
            label: (
              <span>
                <HistoryOutlined /> Runs
              </span>
            ),
            children: (
              <Card>
                <Table
                  columns={columns}
                  dataSource={runs}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} runs`,
                  }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <Space direction="vertical" size="small">
                            <Text strong>No crew runs yet</Text>
                            <Text type="secondary">Start a crew execution to see runs here</Text>
                          </Space>
                        }
                      >
                        <Button type="primary" icon={<RocketOutlined />} href="/run">
                          Run a Crew
                        </Button>
                      </Empty>
                    ),
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'analytics',
            label: (
              <span>
                <BarChartOutlined /> Analytics
              </span>
            ),
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Performance Metrics */}
                <Card title={<><DashboardOutlined /> Performance Metrics</>}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Statistic
                        title="Success Rate"
                        value={analytics.successRate}
                        suffix="%"
                        valueStyle={{ color: '#3f8600' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Failure Rate"
                        value={analytics.failureRate}
                        suffix="%"
                        valueStyle={{ color: '#cf1322' }}
                        prefix={<ExclamationCircleOutlined />}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Avg Duration"
                        value={analytics.avgDuration.toFixed(1)}
                        suffix="sec"
                        prefix={<ClockCircleOutlined />}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="P90 Duration"
                        value={analytics.p90Duration.toFixed(1)}
                        suffix="sec"
                        prefix={<LineChartOutlined />}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Runs by Date */}
                <Card title={<><LineChartOutlined /> Runs by Date</>}>
                  {Object.keys(analytics.runsByDate).length > 0 ? (
                    <Table
                      dataSource={Object.entries(analytics.runsByDate)
                        .map(([date, stats]) => ({ date, ...stats }))
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())}
                      rowKey="date"
                      pagination={false}
                      columns={[
                        { title: 'Date', dataIndex: 'date', key: 'date' },
                        { title: 'Total', dataIndex: 'total', key: 'total', align: 'center' },
                        {
                          title: 'Completed',
                          dataIndex: 'completed',
                          key: 'completed',
                          align: 'center',
                          render: (val: number) => <Text style={{ color: '#3f8600' }}>{val}</Text>,
                        },
                        {
                          title: 'Failed',
                          dataIndex: 'failed',
                          key: 'failed',
                          align: 'center',
                          render: (val: number) => <Text style={{ color: '#cf1322' }}>{val}</Text>,
                        },
                        {
                          title: 'Running',
                          dataIndex: 'running',
                          key: 'running',
                          align: 'center',
                          render: (val: number) => <Text style={{ color: '#1890ff' }}>{val}</Text>,
                        },
                      ]}
                    />
                  ) : (
                    <Empty description="No run data available" />
                  )}
                </Card>

                {/* Runs by Crew */}
                <Card title={<><RobotOutlined /> Runs by Crew</>}>
                  {Object.keys(analytics.runsByCrew).length > 0 ? (
                    <Table
                      dataSource={Object.entries(analytics.runsByCrew).map(([crew, stats]) => ({
                        crew,
                        ...stats,
                        successRate: stats.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(1) : '0',
                      }))}
                      rowKey="crew"
                      pagination={false}
                      columns={[
                        { title: 'Crew', dataIndex: 'crew', key: 'crew' },
                        { title: 'Total Runs', dataIndex: 'total', key: 'total', align: 'center' },
                        {
                          title: 'Completed',
                          dataIndex: 'completed',
                          key: 'completed',
                          align: 'center',
                          render: (val: number) => <Text style={{ color: '#3f8600' }}>{val}</Text>,
                        },
                        {
                          title: 'Failed',
                          dataIndex: 'failed',
                          key: 'failed',
                          align: 'center',
                          render: (val: number) => <Text style={{ color: '#cf1322' }}>{val}</Text>,
                        },
                        {
                          title: 'Success Rate',
                          dataIndex: 'successRate',
                          key: 'successRate',
                          align: 'center',
                          render: (val: string) => `${val}%`,
                        },
                      ]}
                    />
                  ) : (
                    <Empty description="No crew data available" />
                  )}
                </Card>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title="Run Details"
        placement="right"
        width={720}
        open={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
      >
        {selectedRun && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Summary Section */}
            <Card title="Summary" size="small">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Status:</Text>
                  {getStatusTag(selectedRun.status)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Run ID:</Text>
                  <Text code>{selectedRun.id}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Crew:</Text>
                  <Text>{getCrewName(selectedRun.crew_id)}</Text>
                </div>
                {selectedRun.image_model_id && (() => {
                  const meta = getImageModelMeta(selectedRun.image_model_id);
                  if (!meta) return null;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                      <Text strong>Image Model:</Text>
                      <div style={{ textAlign: 'right' }}>
                        <Text>{meta.name}</Text>
                        {meta.subline && (
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {meta.subline}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Started:</Text>
                  <Text>{selectedRun.started_at ? new Date(selectedRun.started_at).toLocaleString() : 'N/A'}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Duration:</Text>
                  <Text>{formatDuration(selectedRun.started_at, selectedRun.completed_at)}</Text>
                </div>
              </Space>
            </Card>

            {selectedRun.wordpress_post && (
              <Card title="WordPress Publication" size="small">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {selectedRun.wordpress_post.title && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                      <Text strong>Title:</Text>
                      <Text style={{ textAlign: 'right' }}>{selectedRun.wordpress_post.title}</Text>
                    </div>
                  )}
                  {selectedRun.wordpress_post.site && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                      <Text strong>Site:</Text>
                      <Text style={{ textAlign: 'right' }}>{selectedRun.wordpress_post.site}</Text>
                    </div>
                  )}
                  {(selectedRun.wordpress_post.status || selectedRun.wordpress_post.published_at) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                      <Text strong>Status:</Text>
                      <Text style={{ textAlign: 'right' }}>
                        {selectedRun.wordpress_post.status || 'Published'}
                        {selectedRun.wordpress_post.published_at && ` • ${new Date(selectedRun.wordpress_post.published_at).toLocaleString()}`}
                      </Text>
                    </div>
                  )}
                  {selectedRun.wordpress_post.url && (
                    <Button type="link" href={selectedRun.wordpress_post.url} target="_blank" rel="noreferrer">
                      Open live post
                    </Button>
                  )}
                  <Popconfirm
                    title="Delete WordPress post?"
                    description="This will remove the published location blog from WordPress."
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true, loading: deletingWordpressRunId === selectedRun.id }}
                    onConfirm={() => handleDeleteWordpressPost(selectedRun)}
                  >
                    <Button danger loading={deletingWordpressRunId === selectedRun.id}>
                      Delete from WordPress
                    </Button>
                  </Popconfirm>
                </Space>
              </Card>
            )}

            {/* Execution Timeline */}
            <Card title="Execution Timeline" size="small">
              <Timeline
                items={getMockSteps(selectedRun).map((step) => ({
                  dot: getStepIcon(step),
                  color: getStepStatusColor(step.status),
                  children: (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Space>
                          <Text strong>{step.name}</Text>
                          {step.status === 'running' && <Tag color="processing">Running</Tag>}
                          {step.status === 'failed' && <Tag color="error">Failed</Tag>}
                        </Space>
                      </div>
                      {step.agent_name && (
                        <div style={{ marginBottom: 4 }}>
                          <Text type="secondary">
                            <RobotOutlined /> {step.agent_name}
                          </Text>
                        </div>
                      )}
                      {step.summary && (
                        <div style={{ marginBottom: 4 }}>
                          <Text type="secondary">{step.summary}</Text>
                        </div>
                      )}
                      {step.duration_ms && (
                        <div>
                          <Text type="secondary">
                            <ClockCircleOutlined /> {formatStepDuration(step.duration_ms)}
                          </Text>
                        </div>
                      )}
                      {step.log && (
                        <Collapse ghost size="small" style={{ marginTop: 8 }}>
                          <Panel header="View logs" key="1">
                            <pre style={{
                              fontSize: 11,
                              backgroundColor: '#f5f5f5',
                              padding: 8,
                              borderRadius: 4,
                              overflow: 'auto',
                              maxHeight: 150
                            }}>
                              {step.log}
                            </pre>
                          </Panel>
                        </Collapse>
                      )}
                    </div>
                  ),
                }))}
              />
            </Card>

            {/* Inputs */}
            {selectedRun.inputs && Object.keys(selectedRun.inputs).length > 0 && (
              <Card title="Inputs" size="small">
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  {JSON.stringify(selectedRun.inputs, null, 2)}
                </pre>
              </Card>
            )}

            {/* Output */}
            {selectedRun.output && (
              <Card title="Output" size="small">
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 400,
                  overflow: 'auto'
                }}>
                  {selectedRun.output}
                </pre>
              </Card>
            )}

            {/* Error */}
            {selectedRun.error && (
              <Card title="Error" size="small">
                <div style={{
                  backgroundColor: '#fff2f0',
                  borderColor: '#ffccc7',
                  padding: 12,
                  borderRadius: 4
                }}>
                  <Text type="danger">{selectedRun.error}</Text>
                </div>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default ResultsPage;
